import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PickemQuestion } from '@low6-pickem/api-types';
import { questionComponentFactory } from '@/factories/questionComponentFactory';
import type { QuestionAdapterContext } from '@/factories/questionComponentFactory.types';
import { useLocalizedFieldGetter } from '@/utils/localized-field';
// Import registry to trigger component registration
import '@/factories/questionComponentRegistry';

interface QuestionRendererProps {
  question: PickemQuestion;
  questionNumber?: number; // Question number for display (1-6), undefined for tiebreaker
  selectedTeams: Record<string, string>;
  selectedProps: Record<string, 'yes' | 'no'>;
  selectedOverUnder: Record<string, 'over' | 'under'>;
  tieBreaker: string;
  onTeamSelect: (questionId: string, teamId: string | undefined) => void;
  onPropSelect: (questionId: string, answer: 'yes' | 'no') => void;
  onOverUnderSelect: (questionId: string, answer: 'over' | 'under' | undefined) => void;
  onTieBreakerChange: (value: string) => void;
  isViewOnly: boolean;
  selectionResults?: Record<string, boolean>;
  selectionPoints?: Record<string, number>;
  /** When absent/false, pick-percentage badges on option cards are hidden. */
  showPopularPicks?: boolean;
}

export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  questionNumber,
  selectedTeams,
  selectedProps,
  selectedOverUnder,
  tieBreaker,
  onTeamSelect,
  onPropSelect,
  onOverUnderSelect,
  onTieBreakerChange,
  isViewOnly,
  selectionResults,
  selectionPoints,
  showPopularPicks,
}) => {
  const { t } = useTranslation();
  const getField = useLocalizedFieldGetter();

  // Get factory config for this question type
  const config = questionComponentFactory.get(question.question_type);

  if (!config) {
    console.warn(`[QuestionRenderer] Unknown question type: ${question.question_type}`);
    return null;
  }

  // Build context for adapter
  const context: QuestionAdapterContext = {
    question,
    questionNumber,
    selections: {
      teams: selectedTeams,
      props: selectedProps,
      overUnder: selectedOverUnder,
      tieBreaker,
    },
    handlers: {
      onTeamSelect,
      onPropSelect,
      onOverUnderSelect,
      onTieBreakerChange,
    },
    state: {
      isViewOnly,
      selectionResults,
      selectionPoints,
      showPopularPicks,
    },
    hooks: {
      t,
      getField,
    },
  };

  // Get component and props from factory
  const Component = config.component;
  const props = config.adapter(context);

  return <Component {...props} />;
};
